<template>
  <div
    class="job-card"
    :class="{ 'job-card--active': isActive }"
  >
    <div
      v-if="isActive"
      class="job-card__bar"
    />

    <router-link
      class="job-card__body"
      :to="detailsRoute"
    >
      <job-status-badge
        :status="job.status"
        :in-flight="job.isCancelable"
      />

      <div
        class="job-card__title"
        :title="job.filePath"
      >
        {{ fileName }}
      </div>
      <div
        v-if="folder"
        class="job-card__folder"
      >
        {{ folder }}
      </div>

      <dl class="job-card__meta">
        <div class="job-card__meta-item">
          <dt>Submitted</dt>
          <dd :title="formatDateTime(job.insertDate)">
            {{ submittedAgo }}
          </dd>
        </div>
        <div class="job-card__meta-item">
          <dt>Finished</dt>
          <dd :title="job.endDateTime ? formatDateTime(job.endDateTime) : ''">
            {{ finishedAgo }}
          </dd>
        </div>
        <div class="job-card__meta-item">
          <dt>Duration</dt>
          <dd>{{ duration }}</dd>
        </div>
      </dl>
    </router-link>

    <b-dropdown
      right
      variant="link"
      no-caret
      toggle-class="job-card__menu-toggle"
      class="job-card__menu"
      menu-class="job-card__menu-list"
    >
      <template #button-content>
        <GjIcon
          name="Moreoptions"
          size="20"
        />
        <span class="sr-only">Job actions</span>
      </template>
      <b-dropdown-item
        v-if="$can('get', 'jobs', 'encoder')"
        :to="detailsRoute"
      >
        View details
      </b-dropdown-item>
      <b-dropdown-item
        v-if="$can('create', 'jobs', 'encoder')"
        :to="{ name: 'clone-job', params: { name: job.name } }"
      >
        Clone
      </b-dropdown-item>
      <b-dropdown-item @click="copyJobId">
        Copy job ID
      </b-dropdown-item>
      <b-dropdown-divider v-if="job.isCancelable" />
      <b-dropdown-item
        v-if="job.isCancelable"
        variant="danger"
        @click="$emit('cancel', job)"
      >
        Cancel job
      </b-dropdown-item>
    </b-dropdown>

    <div class="job-card__footer">
      <span class="job-card__id-label">Job ID</span>
      <span class="job-card__id">{{ job.name }}</span>
      <a
        v-clipboard:copy="job.name"
        v-clipboard:success="copySuccess"
        class="job-card__copy"
        aria-label="Copy job ID"
      >
        <GjIcon
          name="Copy"
          size="18"
        />
      </a>
    </div>
  </div>
</template>

<script>
import JobStatusBadge from './JobStatusBadge.vue'
import { isRunningStatus, splitFilePath, toUtcDate } from '../job-status'

export default {
  name: 'JobMobileCard',
  components: {
    JobStatusBadge,
  },
  props: {
    job: {
      type: Object,
      required: true,
    },
  },
  computed: {
    detailsRoute() {
      return { name: 'jobs-summary', params: { name: this.job.name } }
    },
    isActive() {
      return isRunningStatus(this.job.status, this.job.isCancelable)
    },
    fileName() {
      return splitFilePath(this.job.filePath).fileName || this.job.name
    },
    folder() {
      return splitFilePath(this.job.filePath).folder
    },
    submittedAgo() {
      const ago = this.timeSince(this.job.insertDate)
      return ago ? `${ago} ago` : '-'
    },
    finishedAgo() {
      if (!this.job.endDateTime) return '-'
      const ago = this.timeSince(this.job.endDateTime)
      return ago ? `${ago} ago` : '-'
    },
    duration() {
      const start = toUtcDate(this.job.insertDate)
      const end = toUtcDate(this.job.endDateTime)
      if (!start) return '-'
      if (!end) return this.isActive ? 'Running' : '-'
      const elapsed = end - start
      return elapsed > 0 ? this.formatMilliseconds(elapsed) : '-'
    },
  },
  methods: {
    copyJobId() {
      this.$copyText(this.job.name).then(
        () => this.copySuccess(),
        () => this.errorToast('Could not copy the job ID'),
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.job-card {
  position: relative;
  overflow: hidden;
  margin-bottom: 12px;
  border: 1px solid $gray-1234;
  border-radius: 8px;
  background-color: $white;
  box-shadow: 0 1px 2px rgba(5, 45, 97, 0.04);

  &--active {
    border-color: rgba($primary, 0.35);
  }

  &__bar {
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 2px;
    background-color: $primary;
    animation: job-card-bar 1.6s ease-in-out infinite;
  }

  &__body {
    display: block;
    padding: 14px 48px 12px 14px;
    color: inherit;

    &:hover,
    &:focus {
      color: inherit;
      text-decoration: none;
    }
    // A comfortable tap target for the whole card, keyboard focus stays visible.
    &:focus-visible {
      outline: 2px solid $primary;
      outline-offset: -2px;
    }
  }

  &__title {
    margin-top: 8px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    color: $blue-700;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__folder {
    margin-top: 2px;
    font-size: 12px;
    color: $blue-800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: flex;
    margin: 12px 0 0;
    padding-top: 10px;
    border-top: 1px solid $gray-1234;
  }

  &__meta-item {
    flex: 1 1 0;
    min-width: 0;
    padding-right: 8px;

    &:last-child {
      padding-right: 0;
    }

    dt {
      margin: 0;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: $blue-800;
    }

    dd {
      margin: 2px 0 0;
      font-size: 13px;
      font-weight: 500;
      color: $blue-700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__menu {
    position: absolute;
    top: 6px;
    right: 4px;

    ::v-deep .job-card__menu-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      color: $blue-800;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    padding: 8px 8px 8px 14px;
    border-top: 1px solid $gray-1234;
    background-color: $gray-123;
  }

  &__id-label {
    flex: 0 0 auto;
    margin-right: 8px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: $blue-800;
  }

  &__id {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 12px;
    color: $blue-800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__copy {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: $primary;
  }
}

@keyframes job-card-bar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(250%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .job-card__bar {
    animation: none;
    width: 100%;
    opacity: 0.4;
  }
}
</style>
